import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PackageX, Video, ShieldCheck } from "lucide-react";
import { PageHead, Wrap, H2, P, Prose, Impact, DontList, CheckList, Callout } from "@/components/Content";
import { ChainOfCustody } from "@/components/ChainOfCustody";
import { Seo, breadcrumbSchema } from "@/components/Seo";

export default function HowItWorks() {
  return (
    <div className="bg-ink min-h-screen pb-24">
      <Seo
        title="How It Works — The Submission Process | CardScience HQ"
        description="How to submit a collectible to CardScience HQ: submit online, get evaluated and approved, ship to protocol, and receive documented, protected return. Do not ship before approval."
        path="/how-it-works"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "How It Works", path: "/how-it-works" }])}
      />
      <PageHead eyebrow="How It Works" title={<>DO NOT MAIL US A COLLECTIBLE<br />UNTIL YOUR SUBMISSION IS APPROVED.</>} tone="violet" />

      <Wrap className="py-16">
        <Prose>
          <P>Every card, comic or collectible must first go through the CardScience HQ submission process.</P>
          <P>Customers must receive approval and shipping instructions before sending a collectible.</P>
          <Impact>An unsolicited shipment does not automatically create a service agreement.</Impact>
          <P>A shipment sent outside our approved process may be refused, rejected or returned.</P>
        </Prose>

        <div className="mt-12">
          <p className="eyebrow mb-5">The Journey</p>
          <div className="space-y-4">
            {[
              ["01", "Submit online", "Tell us about your collectible, its category and condition through our submission form."],
              ["02", "We evaluate", "We assess whether your collectible is an appropriate candidate for our process. More than 30% are turned away."],
              ["03", "Approval & instructions", "If approved, you receive your submission approval and exact shipping instructions."],
              ["04", "Ship to protocol", "Package and ship exactly as instructed. No approved submission means no shipment."],
              ["05", "Document, service, document", "We document on intake, perform the approved service, and document again before it leaves our hands."],
              ["06", "Packed, sealed, tracked", "Your collectible is protected, packed, sealed and returned with tracking."],
            ].map(([n, t, d]) => (
              <div key={n} className="flex gap-5 rounded-2xl border border-[color:var(--line)] p-5">
                <span className="font-display font-black text-holo text-2xl shrink-0 w-10">{n}</span>
                <div>
                  <p className="text-white font-semibold">{t}</p>
                  <p className="text-sm text-[color:var(--chrome-3)] mt-1">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14">
          <p className="eyebrow mb-5">Chain Of Custody</p>
          <ChainOfCustody />
        </div>

        <Callout className="mt-14">
          <p className="eyebrow mb-3 flex items-center gap-2"><PackageX size={13} className="text-[color:var(--magenta)]" /> Before You Ship</p>
          <DontList items={[
            "Do not ship any collectible before your submission is approved.",
            "Do not ship if you cannot follow the required shipping and packaging protocol.",
          ]} />
          <P className="mt-4">
            An unsolicited shipment does not obligate CardScience HQ to perform work and may be refused or
            returned. Full requirements are on our Shipping & Insurance page.
          </P>
        </Callout>

        <div className="flex flex-wrap gap-3 mt-12">
          <Link to="/submit" className="btn-holo inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">START A SUBMISSION <ArrowRight size={16} /></Link>
          <Link to="/shipping-insurance" className="btn-ghost inline-flex items-center gap-2 rounded-xl px-7 py-4 text-sm">SHIPPING & INSURANCE</Link>
        </div>
      </Wrap>
    </div>
  );
}
